import { Link } from "@tanstack/react-router";

export const troubleshootingFaq = [
  {
    q: "Is ustraveldocs still used for US visa appointments in India?",
    a: "No. India moved from ustraveldocs.com to usvisascheduling.com. Old ustraveldocs profiles do not carry over — create a fresh account on usvisascheduling.com and pay the MRV fee there.",
  },
  {
    q: "How many times can I reschedule my US visa appointment?",
    a: "The portal caps how many times you can reschedule on one MRV fee and shows your remaining count on the dashboard. Once the limit is used up, you may need to pay a new MRV fee, so change your slot only when necessary.",
  },
  {
    q: "What should I do if usvisascheduling.com shows 'Application in progress' or locks my account?",
    a: "Log out, clear cookies, wait 30–60 minutes and log back in with a single browser tab. Repeated failed security-question answers can freeze the account; use the official help desk contact on the portal to unlock it.",
  },
  {
    q: "Can I attend the OFC and consular interview in different cities?",
    a: "Yes, the portal usually lets you pick OFC (biometrics) and the consular interview in different cities, as long as the OFC date comes before the interview date. Plan travel accordingly.",
  },
];

const sections = [
  {
    id: "ustraveldocs-vs-usvisascheduling",
    title: "ustraveldocs vs usvisascheduling.com: what changed",
    body: [
      "Indian applicants now book only through usvisascheduling.com. The older ustraveldocs.com portal is retired for India.",
      "Profiles, payments and receipts from ustraveldocs do not migrate. Create a new account, link your passport and DS-160, then pay the MRV fee inside the new portal.",
    ],
  },
  {
    id: "rescheduling-limit",
    title: "Rescheduling limit: how many times can you change your slot?",
    body: [
      "Each MRV fee comes with a limited number of reschedules. Your dashboard shows how many remain.",
      "Using all of them can mean paying a fresh MRV fee ($185). Only reschedule once you are sure the new date works for your admit, I-20 start date and travel.",
    ],
  },
  {
    id: "portal-errors",
    title: "Top 4 usvisascheduling.com errors and fixes",
    list: [
      ["Session timeout / logged out mid-booking", "Use one browser tab, avoid refreshing, and keep the page active. Log in again and slots you didn't confirm are not held."],
      ["403 Forbidden / Access denied", "Usually caused by too many refreshes or VPN use. Turn off VPN, clear cookies, wait 30–60 minutes and retry."],
      ["'Application in progress' lock", "Happens when a session didn't close properly. Log out fully, wait, then log back in from a single device."],
      ["Security-question freeze", "Too many wrong answers lock the account. Contact the official help desk listed on the portal to reset access."],
    ],
  },
  {
    id: "emergency-appointment",
    title: "Emergency (expedited) appointment rules for F-1 students",
    body: [
      "You can request an emergency appointment only after booking the earliest available regular slot. Submit the request from your portal dashboard with proof (e.g. I-20 program start date).",
      "Approval is at the consulate's discretion and is not guaranteed. Students often get a denial when a regular slot already exists before the program start date.",
    ],
  },
  {
    id: "ofc-consular-cities",
    title: "Booking OFC and consular interview in different cities",
    body: [
      "The OFC (biometrics) visit and the consular interview can be in different cities — for example OFC in Hyderabad and interview in Chennai.",
      "The OFC date must come before your interview date. Carry the same documents to both, including your appointment confirmation and DS-160 confirmation page.",
    ],
  },
] as const;

export function SchedulingTroubleshooting() {
  return (
    <section id="troubleshooting" className="mx-auto max-w-4xl px-5 mt-16">
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
        usvisascheduling.com troubleshooting (India)
      </h2>
      <p className="mt-3 text-muted-foreground">
        The questions Indian F-1 students ask most once they're inside the portal.
      </p>
      <div className="mt-8 space-y-6">
        {sections.map((s) => (
          <article key={s.id} id={s.id} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-xl font-semibold">{s.title}</h3>
            {"body" in s &&
              s.body.map((p) => (
                <p key={p} className="mt-3 text-sm leading-relaxed text-foreground/85">{p}</p>
              ))}
            {"list" in s && (
              <ul className="mt-4 space-y-3">
                {s.list.map(([t, d]) => (
                  <li key={t} className="text-sm leading-relaxed">
                    <span className="font-semibold text-foreground">{t}:</span>{" "}
                    <span className="text-foreground/85">{d}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
      <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-6">
        <p className="font-display text-lg font-semibold">Slot booked? Now prepare for the interview.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          1-on-1 mock interviews with live DS-160 screen-share review, from ₹3,000.
        </p>
        <Link to="/pricing" className="mt-4 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground">
          See plans
        </Link>
      </div>
    </section>
  );
}
